import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A custom element that displays a dropdown of nested top-bar links
 * (e.g. "Local stores" with multiple store links nested under it).
 *
 * Mirrors DropdownLocalizationComponent's show/hide behavior and reuses
 * the shared `.localization-wrapper` styling, so it matches the
 * localization dropdown's look, colors, and animation exactly.
 *
 * @typedef {object} ToplinkDropdownRefs
 * @property {HTMLButtonElement} button - The toggle button element.
 * @property {HTMLDivElement} panel - The dropdown panel element.
 *
 * @extends {Component<ToplinkDropdownRefs>}
 */
class ToplinkDropdownComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button?.setAttribute('aria-expanded', 'true');
  }

  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

if (!customElements.get('toplink-dropdown-component')) {
  customElements.define('toplink-dropdown-component', ToplinkDropdownComponent);
}